import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_DESTINATION_PARAMS, EMPTY_DESTINATION_PARAMS_LIST_CONFIGURATION, EMPTY_DESTINATION_PARAMS_LIST_CRITERIA } from "../constant/destinationParamsConstant";
import { DestinationParams } from "../objects/destinationParams";
import { DestinationParamsListCriteria } from "../objects/destinationParamsListCriteria";
import { DestinationParamsState } from "../objects/destinationParamsState";
import { setDestinationParamsPreference } from "../utils/setDestinationParamsPreference";
import { Column } from "../objects/column";
import { DepartureAirport } from "../../departureAirport/objects/departureAirport";
import { applyColumnResize, ColumnResizePayload } from "../../utils/list/utils/columnResize";


const initialState: DestinationParamsState = {
    destination_params_list: [],
    destination_params_list_configuration: EMPTY_DESTINATION_PARAMS_LIST_CONFIGURATION,
    destination_params_list_loading: false,
    destination_params_list_count: 0,
    destination_params_list_criteria: EMPTY_DESTINATION_PARAMS_LIST_CRITERIA,
    destination_params: EMPTY_DESTINATION_PARAMS,
    first_preference_loaded: false,
    departure_airports: []
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const addDestinationParamsListConfigurationPreferenceReducer = (state: DestinationParamsState, action: PayloadAction<Preferences[]>): DestinationParamsState => ({
    ...state, destination_params_list_configuration: { ...state.destination_params_list_configuration, preferences: [...state.destination_params_list_configuration.preferences, ...action.payload] }
});

const setDestinationParamsListConfigurationPreferenceReducer = (state: DestinationParamsState, action: PayloadAction<Preferences[]>): DestinationParamsState => ({
    ...state, destination_params_list_configuration: { ...state.destination_params_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: DestinationParamsState, action: PayloadAction<Preferences[]>): DestinationParamsState => {
    const { preferences, columns, order_by, order } = setDestinationParamsPreference(action.payload, state.destination_params_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        destination_params_list_configuration: { ...state.destination_params_list_configuration, preferences, columns },
        destination_params_list_criteria: { ...state.destination_params_list_criteria, order, order_by }
    };
};

const moveDownColumnReducer = (state: DestinationParamsState, action: PayloadAction<number>): DestinationParamsState => {
    const columns = cloneDeep([...state.destination_params_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, destination_params_list_configuration: { ...state.destination_params_list_configuration, columns } };
};


const moveUpColumnReducer = (state: DestinationParamsState, action: PayloadAction<number>): DestinationParamsState => {
    const columns = cloneDeep([...state.destination_params_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, destination_params_list_configuration: { ...state.destination_params_list_configuration, columns } };
};

const toggleColumnReducer = (state: DestinationParamsState, action: PayloadAction<number>): DestinationParamsState => {
    const columns = cloneDeep([...state.destination_params_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, destination_params_list_configuration: { ...state.destination_params_list_configuration, columns } };
};

const updateDestinationParamsListReducer = (state: DestinationParamsState, action: PayloadAction<DestinationParams[]>): DestinationParamsState => ({
    ...state, destination_params_list: action.payload
});
const updateDestinationParamsListCriteriaReducer = (state: DestinationParamsState, action: PayloadAction<DestinationParamsListCriteria>): DestinationParamsState => ({
    ...state, destination_params_list_criteria: action.payload
});
const updateDestinationParamsReducer = (state: DestinationParamsState, action: PayloadAction<DestinationParams>): DestinationParamsState => ({
    ...state,
    destination_params: {
        ...action.payload,
        assigned_trip: action.payload.assigned_trip ?? 'ALL',
        max_stops: action.payload.max_stops ?? '',
        max_hour_spent_on_stopover: action.payload.max_hour_spent_on_stopover ?? '',
        start_arrival_time: action.payload.start_arrival_time ?? '',
        end_arrival_time: action.payload.end_arrival_time ?? '',
        start_departure_time: action.payload.start_departure_time ?? '',
        end_departure_time: action.payload.end_departure_time ?? '',
        start_arrival_time_inbound: action.payload.start_arrival_time_inbound ?? '',
        end_arrival_time_inbound: action.payload.end_arrival_time_inbound ?? '',
        start_departure_time_inbound: action.payload.start_departure_time_inbound ?? '',
        end_departure_time_inbound: action.payload.end_departure_time_inbound ?? '',
        allow_number_of_allotment: action.payload.allow_number_of_allotment ?? '',
        flight_duration_min: action.payload.flight_duration_min ?? '',
        flight_duration_max: action.payload.flight_duration_max ?? '',
        allowed_client_type: action.payload.allowed_client_type ?? [],
        period: action.payload.period.map((period) => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })),
        providers: action.payload.providers || [],
        allowed_airline: action.payload.allowed_airline || [],
        disallowed_airline: action.payload.disallowed_airline || [],
        number_of_bags: action.payload.number_of_bags || [],
        allowed_stopovers_airport: action.payload.allowed_stopovers_airport || [],
        disallowed_stopovers_airport: action.payload.disallowed_stopovers_airport || [],
        allowed_departure_airport: action.payload.allowed_departure_airport || [],
        disallowed_departure_airport: action.payload.disallowed_departure_airport || [],
        allowed_arrival_airport: action.payload.allowed_arrival_airport || [],
        disallowed_arrival_airport: action.payload.disallowed_arrival_airport || [],
        allowed_return_airport: action.payload.allowed_return_airport || null,
        allowed_stopovers_list: action.payload.allowed_stopovers_list || null,
        disallowed_stopovers_list: action.payload.disallowed_stopovers_list || null,
        allowed_departure_list: action.payload.allowed_departure_list || null,
        disallowed_departure_list: action.payload.disallowed_departure_list || null,
        allowed_arrival_list: action.payload.allowed_arrival_list || null,
        disallowed_arrival_list: action.payload.disallowed_arrival_list || null,
        departure_dow: action.payload.departure_dow || [],
        without_transport_arrival_dow: action.payload.without_transport_arrival_dow || [],
        arrival_dow: action.payload.arrival_dow || [],
        cabin: action.payload.cabin || [],
        tariff_letters: action.payload.tariff_letters || []
    }
});
const updateDestinationParamsListLoadingReducer = (state: DestinationParamsState, action: PayloadAction<boolean>): DestinationParamsState => ({
    ...state, destination_params_list_loading: action.payload
});
const updateDestinationParamsListCountReducer = (state: DestinationParamsState, action: PayloadAction<number>): DestinationParamsState => ({
    ...state, destination_params_list_count: action.payload
});
const setColumnsReducer = (state: DestinationParamsState, action: PayloadAction<Column[]>): DestinationParamsState => ({
    ...state, destination_params_list_configuration: { ...state.destination_params_list_configuration, columns: action.payload },
});
const setDepartureAirportsReducer = (state: DestinationParamsState, action: PayloadAction<DepartureAirport[]>): DestinationParamsState => ({
    ...state, departure_airports: action.payload,
});

export const destinationParams = createSlice({
    name: 'destination_params',
    initialState,
    reducers: {
        addDestinationParamsListConfigurationPreference: addDestinationParamsListConfigurationPreferenceReducer,
        setDestinationParamsListConfigurationPreference: setDestinationParamsListConfigurationPreferenceReducer,
        moveDownColumn: moveDownColumnReducer,
        moveUpColumn: moveUpColumnReducer,
        toggleColumn: toggleColumnReducer,
        updateDestinationParamsList: updateDestinationParamsListReducer,
        updateDestinationParamsListCriteria: updateDestinationParamsListCriteriaReducer,
        updateDestinationParams: updateDestinationParamsReducer,
        updateDestinationParamsListLoading: updateDestinationParamsListLoadingReducer,
        updateDestinationParamsListCount: updateDestinationParamsListCountReducer,
        setColumns: setColumnsReducer,
        setDepartureAirports: setDepartureAirportsReducer,
        resizeColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.destination_params_list_configuration.columns =
                applyColumnResize(state.destination_params_list_configuration.columns, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    addDestinationParamsListConfigurationPreference,
    setDestinationParamsListConfigurationPreference,
    moveDownColumn,
    moveUpColumn,
    toggleColumn,
    updateDestinationParamsList,
    updateDestinationParamsListCriteria,
    updateDestinationParams,
    updateDestinationParamsListLoading,
    updateDestinationParamsListCount,
    setColumns,
    setDepartureAirports,
    resizeColumn
} = destinationParams.actions;